import React from "react"
import {
    useNavigate
} from "react-router-dom"
import ConnectUs from "./ContactUs";
import { Typography } from "@mui/material";

const LandingPage = () => {
    const navigate = useNavigate()
    const [isConnect, setConnect] = React.useState(false)


    return (
        <div className="font-sans text-slate-800 bg-slate-50 min-h-screen">
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 py-24 px-4 sm:px-6 lg:px-8 text-center">
                <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.2),transparent_45%)]"></div>
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-500 rounded-full blur-[100px] opacity-20"></div>
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-600 rounded-full blur-[100px] opacity-20"></div>

                <div className="relative mx-auto max-w-4xl text-center z-10">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse"></span>
                        Next-Gen Academic Platform
                    </span>
                    <h1 className="mb-6 text-4xl font-extrabold tracking-tight leading-tight text-white md:text-5xl lg:text-6xl font-heading">
                        Simplify School & College <br />
                        <span className="bg-gradient-to-r from-indigo-400 via-violet-300 to-indigo-300 bg-clip-text text-transparent">Management</span>
                    </h1>
                    <p className="mb-10 text-lg font-normal text-slate-300 max-w-2xl mx-auto leading-relaxed">
                        Manage students, classes, attendance, exams, fees, and academic workflows — all inside one highly intuitive, modern, production-ready dashboard.
                    </p>
                    <div className="flex flex-col space-y-4 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
                        <a href="#" className="inline-flex justify-center items-center py-3.5 px-7 text-base font-semibold text-center text-white rounded-xl bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-600/30 hover:shadow-indigo-500/40 transition-all duration-200"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                navigate("/auth")
                            }}
                        >
                            Get started
                            <svg className="w-4 h-4 ms-2 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M1 5h12m0 0L9 1m4 4L9 9" />
                            </svg>
                        </a>
                        <a href="#features" className="inline-flex justify-center items-center py-3.5 px-7 text-base font-semibold text-center text-slate-200 hover:text-white rounded-xl border border-slate-700 hover:border-slate-500 hover:bg-slate-800/40 transition-all duration-200">
                            Learn more
                        </a>
                        {/* Entry point to the student lecture portal, which has its own sign-in */}
                        <a href="/learn" className="inline-flex justify-center items-center py-3.5 px-7 text-base font-semibold text-center text-slate-200 hover:text-white rounded-xl border border-slate-700 hover:border-slate-500 hover:bg-slate-800/40 transition-all duration-200"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                navigate("/learn")
                            }}
                        >
                            Student lectures
                        </a>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="px-6 md:px-16 py-24 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-sm font-bold tracking-wider text-indigo-600 uppercase mb-3">Key Features</h2>
                    <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900 font-heading">
                        Engineered for Modern Institutions
                    </h3>
                    <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
                        Everything you need to automate administrative tasks, handle online payments, schedule exams, and digitize certificate delivery.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {[
                        {
                            title: "Student Information System",
                            desc: "Maintain comprehensive student profiles, academic histories, document uploads, and daily attendance logs in one database.",
                            key: "f1",
                            color: "from-blue-500 to-indigo-500"
                        },
                        {
                            title: "Teacher Portal",
                            desc: "Provide teachers with simplified dashboard tooling to publish grades, track course timelines, and coordinate parent communication.",
                            key: "f2",
                            color: "from-purple-500 to-indigo-500"
                        },
                        {
                            title: "Analytics & Smart Reporting",
                            desc: "Generate academic progress summaries, enrollment trends, and financial collection reports with powerful exports.",
                            key: "f3",
                            color: "from-pink-500 to-rose-500"
                        },
                        {
                            title: "Exam & Grading Center",
                            desc: "Configure sessional or terminal examination papers, define scale rules, and publish grading records in real-time.",
                            key: "f4",
                            color: "from-amber-500 to-orange-500"
                        },
                        {
                            title: "Digital Admission & Intake",
                            desc: "Automate open registration campaigns, accept documents digitally, and onboard students through customized workflows.",
                            key: "f5",
                            color: "from-emerald-500 to-teal-500"
                        },
                        {
                            title: "Online Admit Card Generation",
                            desc: "Create standardized digital admit cards complete with automatically generated student QR codes for fast exam validation.",
                            key: "f6",
                            color: "from-cyan-500 to-blue-500"
                        },
                        {
                            title: "Secure Digital Certificates",
                            desc: "Issue Bonafide, Transfer, Character, or Leaving certificates on securely verified templates with a single approval click.",
                            key: "f7",
                            color: "from-violet-500 to-fuchsia-500"
                        },
                        {
                            title: "Student & Parent Dashboard",
                            desc: "Help students easily keep tabs on their daily timetables, assignment deadlines, fee balances, and institutional noticeboards.",
                            key: "f8",
                            color: "from-rose-500 to-red-500"
                        },
                        {
                            title: "Event & Activity Manager",
                            desc: "Plan upcoming events, register participants, broadcast schedule modifications, and keep your campus community engaged.",
                            key: "f9",
                            color: "from-teal-500 to-emerald-500"
                        }
                    ].map((f, i) => (
                        <div key={i} className="group relative flex flex-col p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center text-white mb-6 shadow-md shadow-indigo-500/10`}>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-indigo-600 transition-colors duration-200 font-heading">
                                {f.title}
                            </h4>
                            <p className="text-slate-600 leading-relaxed mb-6 flex-grow">
                                {f.desc}
                            </p>
                            <a href={`/feature/${f.key}`} className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-150">
                                Explore feature
                                <svg className="w-4 h-4 ms-1 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                                </svg>
                            </a>
                        </div>
                    ))}
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-24 bg-slate-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.15),transparent_60%)]"></div>
                <div className="relative max-w-4xl mx-auto px-6 text-center z-10">
                    <span className="text-xs font-bold uppercase tracking-widest text-indigo-400">Our Mission</span>
                    <h3 className="text-3xl md:text-4xl font-extrabold mt-4 mb-6 font-heading">About schoolcare.life</h3>
                    <p className="text-slate-300 text-lg leading-relaxed max-w-2xl mx-auto">
                        eduCare is built to empower learning institutions by dismantling legacy administrative bottlenecks. We automate day-to-day operations so educators can shift focus back to what truly matters: mentoring students and elevating the standard of academic excellence.
                    </p>
                    <button className="bg-white text-indigo-600 font-bold px-8 py-3.5 rounded-xl hover:bg-slate-50 shadow-md transition-colors duration-150"
                        onClick={() => {
                            navigate("/about-us")
                        }}
                    >
                        About Us
                    </button>
                </div>
            </section>

            {/* Testimonials Section */}
            <section id="testimonials" className="py-24 bg-slate-50 max-w-7xl mx-auto px-6 md:px-16">
                <div className="text-center mb-16">
                    <h2 className="text-sm font-bold tracking-wider text-indigo-600 uppercase mb-3">Feedback</h2>
                    <h3 className="text-3xl font-extrabold text-slate-900 font-heading">Trusted by Educators</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { name: "Dr. Rajesh Sharma", role: "Principal, DAV High", msg: "eduCare has completely transformed our fee management. Manual errors are down to absolute zero." },
                        { name: "Srinivasan Nair", role: "Science Teacher", msg: "Entering class marks and publishing term results is now a matter of three clicks." },
                        { name: "Student Council", role: "Modern Academy", msg: "Having our timetables, admit cards, and test records in one mobile dashboard is incredibly helpful." },
                    ].map((t, i) => (
                        <div key={i} className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-center gap-1 text-amber-400 mb-4">
                                {[...Array(5)].map((_, idx) => (
                                    <svg key={idx} className="w-5 h-5 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="italic text-slate-600 mb-6 leading-relaxed">"{t.msg}"</p>
                            <div>
                                <h4 className="font-bold text-slate-900">{t.name}</h4>
                                <span className="text-xs font-semibold text-slate-500">{t.role}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Contact Call to Action */}
            <section id="contact" className="py-20 px-6 max-w-7xl mx-auto mb-20">
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-3xl p-12 text-white text-center shadow-xl shadow-indigo-600/20 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.4),transparent_50%)]"></div>
                    <h3 className="text-3xl md:text-4xl font-extrabold mb-4 font-heading">Ready to Digitalize Your Campus?</h3>
                    <p className="mb-8 text-indigo-100 max-w-lg mx-auto text-lg">
                        Get in touch with our solutions engineers to build a custom migration plan for your school or college.
                    </p>
                    <button className="bg-white text-indigo-600 font-bold px-8 py-3.5 rounded-xl hover:bg-slate-50 shadow-md transition-colors duration-150"
                        onClick={() => {
                            navigate("/connect_with_us")
                        }}
                    >
                        Contact Us
                    </button>
                </div>
            </section>

            <ConnectUs
                open={isConnect}
                onClose={() => {
                    setConnect(false)
                }}
            />

            {/* Footer */}
            <footer className="border-t border-slate-200 bg-white py-12 text-center text-slate-500 text-sm">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <Typography variant="body2" sx={{ fontFamily: "var(--font-primary)", color: "var(--text-secondary)" }}>
                        © {new Date().getFullYear()} eduCare. All rights reserved.
                    </Typography>
                    <div className="flex gap-6">
                        <a href="#" className="hover:text-indigo-600 transition-colors">Privacy Policy</a>
                        <a href="#" className="hover:text-indigo-600 transition-colors">Terms of Service</a>
                        <a href="#" className="hover:text-indigo-600 transition-colors">Documentation</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
